/**
 * Agent-published sites in Work transcripts.
 *
 * The execution host emits `site_published` / `site_unpublished` events with
 * the control plane's public site record. Links rendered from events must
 * point at the sites domain only, so a malformed event can never produce a
 * clickable link elsewhere.
 */

export const SITES_DOMAIN = String(process.env.EXPO_PUBLIC_SITES_DOMAIN || 'sites.gabusercontent.com').toLowerCase();

const NAME = /^[a-z0-9](?:[a-z0-9-]{1,38}[a-z0-9])$/;

export function publishedSiteUrl(name) {
  return `https://${name}.${SITES_DOMAIN}/`;
}

function isSiteUrl(url, name) {
  try {
    const parsed = new URL(String(url));
    const localDev = parsed.protocol === 'http:' && SITES_DOMAIN.endsWith('.localhost');
    return (parsed.protocol === 'https:' || localDev) && parsed.hostname === `${name}.${SITES_DOMAIN}`;
  } catch {
    return false;
  }
}

export function slimPublishedSite(site) {
  if (!site || typeof site !== 'object') return null;
  const name = String(site.name || '').toLowerCase();
  if (!NAME.test(name) || name.includes('--')) return null;
  const url = isSiteUrl(site.url, name) ? String(site.url) : publishedSiteUrl(name);
  const expiresAt = site.expiresAt && Number.isFinite(Date.parse(site.expiresAt)) ? new Date(site.expiresAt).toISOString() : null;
  const status = ['live', 'expired', 'unpublished', 'removed'].includes(site.status) ? site.status : 'live';
  const title = typeof site.title === 'string' ? site.title.replace(/[\u0000-\u001f<>]/g, ' ').trim().slice(0, 80) || null : null;
  const fileCount = Number.isSafeInteger(site.fileCount) && site.fileCount >= 0 ? site.fileCount : 0;
  const description = typeof site.description === 'string' ? site.description.replace(/[\u0000-\u001f<>]/g, ' ').trim().slice(0, 240) : null;
  const imageAlt = typeof site.imageAlt === 'string' ? site.imageAlt.slice(0, 240) : title;
  let imageUrl = null;
  try {
    const parsed = new URL(site.imageUrl);
    if (isSiteUrl(site.imageUrl, name) && parsed.origin === new URL(url).origin && !parsed.username && !parsed.password && !parsed.search && !parsed.hash && /^\/__gab\/share\/[a-f0-9]{64}\.jpg$/.test(parsed.pathname)) imageUrl = parsed.href;
  } catch { /* No valid share image on older deployments. */ }
  return { name, url, status, expiresAt, title, fileCount, description, imageAlt, imageUrl };
}

/** Latest known state per site name, in first-published order. */
export function publishedSitesFromEvents(events) {
  const byName = new Map();
  for (const ev of Array.isArray(events) ? events : []) {
    if (ev?.type !== 'site_published' && ev?.type !== 'site_unpublished') continue;
    const site = slimPublishedSite(ev.site);
    if (!site) continue;
    const next = ev.type === 'site_unpublished' ? { ...site, status: 'unpublished' } : { ...site, redeployed: !!ev.redeployed };
    byName.set(site.name, next);
  }
  return [...byName.values()];
}

export function effectiveSiteStatus(site, now = Date.now()) {
  if (!site) return 'expired';
  if (site.status === 'live' && site.expiresAt && Date.parse(site.expiresAt) <= now) return 'expired';
  return site.status;
}

export function siteExpiryLabel(site, now = Date.now()) {
  const status = effectiveSiteStatus(site, now);
  if (status === 'unpublished') return 'Unpublished';
  if (status === 'removed') return 'Removed by Gab AI';
  if (!site?.expiresAt) return status === 'expired' ? 'Expired' : 'Online';
  const at = Date.parse(site.expiresAt);
  if (status === 'expired') return 'Expired';
  const hours = Math.max(0, Math.round((at - now) / 3600000));
  if (hours < 1) return 'Online for less than an hour';
  if (hours < 48) return `Online for ${hours} more hour${hours === 1 ? '' : 's'}`;
  const days = Math.round(hours / 24);
  return `Online for ${days} more days`;
}

// Accepts "name", "name.sites.gabusercontent.com" or a full site URL.
export function siteNameFromInput(value) {
  let text = String(value || '').trim().toLowerCase();
  try { if (/^https?:\/\//.test(text)) text = new URL(text).hostname; } catch { /* keep text */ }
  text = text.replace(/\/.*$/, '');
  for (const suffix of [`.c.${SITES_DOMAIN}`, `.${SITES_DOMAIN}`]) if (text.endsWith(suffix)) text = text.slice(0, -suffix.length);
  return NAME.test(text) && !text.includes('--') ? text : null;
}
